import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

// All pages that rely on 3rd party components (other than Bootstrap) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Layouts
import AuthLayout from "./layouts/Auth";
import OnboardingLayout from "./layouts/Onboarding";
import ConsentLayout from "./layouts/Consent";
import ESignatureLayout from "./layouts/ESignature";
import DocumentLayout from "./layouts/Document";
import DisclosureLayout from "./layouts/Disclosures";
import FppeProviderLayout from "./layouts/FppeProvider";
import FppeReviewerLayout from "./layouts/FppeReviewer";
import NetworkLayout from "./layouts/Network";
import DashboardLayout from "./layouts/Dashboard";
import H3AdminLayout from "./layouts/H3Admin";

// Guards
import AuthGuard from "./components/guards/AuthGuard";
import H3AdminGuard from "./components/guards/H3AdminGuard";

// Dashboards
import Default from "./pages/dashboards/Default";


// Auth
import Page500 from "./pages/auth/Page500";
import Page404 from "./pages/auth/Page404";
import SignIn from "./pages/auth/SignIn";
import ResetPassword from "./pages/auth/ResetPassword";
import SetPassword from "./pages/auth/SetPassword";

// Onboarding
import Onboarding from "./pages/onboarding/Onboarding";
import PracticeOnboarding from "./pages/onboarding/PracticeOnboarding";

// Consent
import Consent from "./pages/consent/Consent";

// E-Signature
import ESignature from "./pages/esignature/ESignature";

// Document
import Document from "./pages/document/Document";

// Disclosures
import Disclosures from "./pages/disclosure/Disclosures";

// FPPE
import FppeProvider from "./pages/fppe/FppeProvider";
import FppeReviewer from "./pages/fppe/FppeReviewer";

// Network
import Network from "./pages/network/Network";

// Privileges
import Privileges from "./pages/privileges/Privileges";

import Practice from "./pages/Practice"
import Provider from "./pages/Provider"
import Tasks from "./pages/Tasks";
import FppeTasks from "./pages/FppeTasks";
import ProviderManagement from "./pages/admin/ProviderManagement";
import PracticeManagement from "./pages/admin/PracticeManagement";
import UserManagement from "./pages/admin/UserManagement";
import CarrierManagement from "./pages/admin/CarrierManagement";
import FacilityManagement from "./pages/admin/FacilityManagement";
import RevenueTeamManagement from "./pages/admin/RevenueTeamManagement";
import RevenueTeams from "./pages/h3admin/RevenueTeams";
import Messaging from "./pages/Messaging";
import MessagingTemplates from "./pages/MessagingTemplates";
import Profile from "./pages/Profile";

import KPIs from "./pages/reports/KPIs";
import KPIsV2 from "./pages/reports/KPIsV2";
import Rosters from "./pages/reports/Rosters";
import ProviderReports from "./pages/reports/ProviderReports";
import PracticeReports from "./pages/reports/PracticeReports";
import TaskReports from "./pages/reports/TaskReports";
import AuditReports from "./pages/reports/AuditReports";

// Protected routes
// import ProtectedPage from "./pages/protected/ProtectedPage";

const TokenRedirect = ({path}) => {
  const { token } = useParams();
  const location = useLocation();

  return <Navigate to={`/network/carrier/${token}${location.search}`} replace />;
}

const routes = [
  {
    path: "/",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Default />,
      },
    ],
  },
  {
    path: "practice/:id",
    element:  (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Practice />,
      },
    ],
  },
  {
    path: "provider/:id",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Provider />,
      },
    ],
  },
  {
    path: "tasks",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Tasks />,
      },
    ],
  },
  {
    path: "fppetasks",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <FppeTasks />,
      },
    ],
  },
  {
    path: "reports/kpis_saas",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <KPIs />,
      },
    ],
  },
  {
    path: "reports/kpis",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <KPIsV2 />,
      },
    ],
  },
  {
    path: "reports/roster",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Rosters />,
      },
    ],
  },
  {
    path: "reports/provider",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProviderReports />,
      },
    ],
  },
  {
    path: "reports/practice",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <PracticeReports />,
      },
    ],
  },
  {
    path: "reports/task",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <TaskReports />,
      },
    ],
  },
  {
    path: "reports/audit",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <AuditReports />,
      },
    ],
  },
  {
    path: "messaging",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Messaging />,
      },
    ],
  },
  {
    path: "messagingtemplates",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <MessagingTemplates />,
      },
    ],
  },
  {
    path: "profile",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Profile />,
      },
    ],
  },
  {
    path: "admin/providers",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProviderManagement />,
      },
    ],
  },
  {
    path: "admin/practices",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <PracticeManagement />,
      },
    ],
  },
  {
    path: "admin/users",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <UserManagement />,
      },
    ],
  },
  {
    path: "admin/carriers",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <CarrierManagement />,
      },
    ],
  },
  {
    path: "admin/locations",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <FacilityManagement />,
      },
    ],
  },
    {
    path: "admin/revenueteams",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <RevenueTeamManagement />,
      },
    ],
  },
  {
    path: "h3admin/revenueteams",
    element: (
      <AuthGuard>
        <H3AdminGuard>
          <H3AdminLayout />
        </H3AdminGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <RevenueTeams />,
      },
    ],
  },
  {
    path: "password",  // TODO move other auth stuff up here
    element: <AuthLayout />,
    children: [
      {
        path: "",
        element: <SetPassword />,
      },
      {
        path: ":token",
        element: <SetPassword />,
      }
    ]
  },
  {
    path: "auth",
    element: <AuthLayout />,
    children: [
      {
        path: "sign-in",
        element: <SignIn />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "404",
        element: <Page404 />,
      },
      {
        path: "500",
        element: <Page500 />,
      },
    ],
  },
  {
    path: "onboarding",
    element: <OnboardingLayout />,
    children: [
      {
        path: ":stage/:token",
        element: <Onboarding />,
      },
      {
        path: ":stage",
        element: <Onboarding />,
      }
    ]
  },
  {
    path: "practice-onboarding",
    element: <OnboardingLayout />,
    children: [
      {
        path: ":stage/:token",
        element: <PracticeOnboarding />,
      },
      {
        path: ":stage",
        element: <PracticeOnboarding />,
      }
    ]
  },
  {
    path: "consent",
    element: <ConsentLayout />,
    children: [
      {
        path: ":token",
        element: <Consent />,
      }
    ]
  },
  {
    path: "esignature",
    element: <ESignatureLayout />,
    children: [
      {
        path: ":token",
        element: <ESignature />,
      }
    ]
  },
  {
    path: "document",
    element: <DocumentLayout />,
    children: [
      {
        path: ":token",
        element: <Document />,
      }
    ]
  },
  {
    path: "disclosures",
    element: <DisclosureLayout />,
    children: [
      {
        path: "provider/:token",
        element: <Disclosures />,
      },
      {
        path: "admin/:token",
        element: <Disclosures />,
      }
    ]
  },
  {
    path: "fppe/provider",
    element: <FppeProviderLayout />,
    children: [
      {
        path: ":token",
        element: <FppeProvider />,
      }
    ]
  },
  {
    path: "fppe/reviewer",
    element: <FppeReviewerLayout />,
    children: [
      {
        path: ":token",
        element: <FppeReviewer />,
      }
    ]
  },
  {
    path: "network",
    children: [
      {
        path: ":token", // Catch any arbitrary token directly under /network/
        element: <TokenRedirect />,
      },
      {
        path: ":by",
        element: <NetworkLayout />,
        children: [
          {
            path: ":token",
            element: <Network />,
          },
        ],
      },
    ]
  },
  // {
  //   path: "network/:by",
  //   element: <NetworkLayout />,
  //   children: [
  //     {
  //       path: ":token",
  //       element: <Network />,
  //     }
  //   ]
  // },
  {
    path: "privileges/:by",
    element: <NetworkLayout />,
    children: [
      {
        path: ":token",
        element: <Privileges />,
      }
    ]
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

export default routes;
